import React, { useState, useEffect } from "react";
import moment from "moment";
import Header from "../header/header";
import { Button, Table, Row, Col, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import app from "../../firebase";
import { collection, getDocs, query, orderBy, getFirestore, doc, writeBatch, updateDoc, deleteDoc } from "firebase/firestore";
import styles from "./stats.module.scss";

export const StatsView = () => {
  const [allStats, setAllStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [uniqueEvents, setUniqueEvents] = useState([]);
  const [uniqueTypes, setUniqueTypes] = useState([]);
  const [uniqueSeasons, setUniqueSeasons] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showMassDeleteModal, setShowMassDeleteModal] = useState(false);
  const [massDeleteCount, setMassDeleteCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newTypeValue, setNewTypeValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const PAGE_SIZE = 100; // Display 100 rows per page

  // Define the exact column order for the table
  const tableColumns = [
    "playerProfileId",
    "date",
    "fullName",
    "season",
    "event",
    "type",
    "team",
    "opponent",
    "result",
    "min",
    "fgFga",
    "fgPct",
    "threePThreePA",
    "threeFgPct",
    "ftFta",
    "ftPct",
    "orb",
    "drb",
    "trb",
    "ast",
    "blk",
    "stl",
    "to",
    "pf",
    "pts",
    "source",
    "dateAdded"
  ];

  useEffect(() => {
    fetchAllStats();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [allStats, dateFilter, eventFilter, typeFilter, seasonFilter]);

  const fetchAllStats = async () => {
    setIsLoading(true);
    try {
      const firestore = getFirestore(app);
      const statsCollection = collection(firestore, "game-log-stats");
      const statsQuery = query(statsCollection, orderBy("date", "desc"));
      const querySnapshot = await getDocs(statsQuery);
      
      const statsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAllStats(statsData);
      
      // Extract unique events for filter dropdown
      const events = [...new Set(statsData.map(stat => stat.event).filter(Boolean))].sort();
      setUniqueEvents(events);
      
      // Extract unique types for filter dropdown
      const types = [...new Set(statsData.map(stat => stat.type).filter(Boolean))].sort();
      setUniqueTypes(types);
      
      // Extract unique seasons for filter dropdown
      const seasons = [...new Set(statsData.map(stat => stat.season).filter(Boolean))].sort();
      setUniqueSeasons(seasons);
      
      toast.success(`Loaded ${statsData.length.toLocaleString()} stats records`);
    } catch (err) {
      console.error(err);
      // If orderBy fails (field might not be indexed), try without ordering
      try {
        const firestore = getFirestore(app);
        const statsCollection = collection(firestore, "game-log-stats");
        const querySnapshot = await getDocs(statsCollection);
        
        const statsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort client-side by date
        statsData.sort((a, b) => {
          const dateA = a.date ? moment(a.date, "MM/DD/YYYY") : moment(0);
          const dateB = b.date ? moment(b.date, "MM/DD/YYYY") : moment(0);
          return dateB.valueOf() - dateA.valueOf();
        });

        setAllStats(statsData);
        
        // Extract unique events for filter dropdown
        const events = [...new Set(statsData.map(stat => stat.event).filter(Boolean))].sort();
        setUniqueEvents(events);
        
        // Extract unique types for filter dropdown
        const types = [...new Set(statsData.map(stat => stat.type).filter(Boolean))].sort();
        setUniqueTypes(types);
        
        // Extract unique seasons for filter dropdown
        const seasons = [...new Set(statsData.map(stat => stat.season).filter(Boolean))].sort();
        setUniqueSeasons(seasons);
        
        toast.success(`Loaded ${statsData.length.toLocaleString()} stats records`);
      } catch (fallbackErr) {
        console.error(fallbackErr);
        toast.error(`Failed to load stats: ${fallbackErr.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allStats];

    // Filter by date
    if (dateFilter) {
      const filterDate = moment(dateFilter, "YYYY-MM-DD");
      filtered = filtered.filter((stat) => {
        if (!stat.date) return false;
        const statDate = moment(stat.date, "MM/DD/YYYY");
        return statDate.isSame(filterDate, "day");
      });
    }

    // Filter by event
    if (eventFilter) {
      filtered = filtered.filter((stat) => 
        stat.event && stat.event.toLowerCase().includes(eventFilter.toLowerCase())
      );
    }

    // Filter by type
    if (typeFilter) {
      filtered = filtered.filter((stat) => 
        stat.type && stat.type.toLowerCase().includes(typeFilter.toLowerCase())
      );
    }

    // Filter by season
    if (seasonFilter) {
      filtered = filtered.filter((stat) => 
        stat.season && stat.season === seasonFilter
      );
    }

    setFilteredStats(filtered);
  };

  const handleClearFilters = () => {
    setDateFilter("");
    setEventFilter("");
    setTypeFilter("");
    setSeasonFilter("");
  };

  const hasActiveFilters = dateFilter || eventFilter || typeFilter || seasonFilter;

  // Count records matching specific events and season for mass delete
  const countMatchingRecords = (events, season) => {
    return allStats.filter(stat => {
      const matchesEvent = events.some(event => 
        stat.event && stat.event.trim() === event.trim()
      );
      const matchesSeason = !season || (stat.season && stat.season === season);
      return matchesEvent && matchesSeason;
    }).length;
  };

  // Handle mass delete confirmation
  const handleMassDeleteConfirm = () => {
    const eventsToDelete = ["3SSB 15U", "3SSB 16U", "3SSB 17U"];
    const seasonToDelete = "2025";
    const count = countMatchingRecords(eventsToDelete, seasonToDelete);
    setMassDeleteCount(count);
    setShowMassDeleteModal(true);
  };

  // Execute mass delete
  const handleMassDelete = async () => {
    const eventsToDelete = ["3SSB 15U", "3SSB 16U", "3SSB 17U"];
    const seasonToDelete = "2025";
    
    setIsDeleting(true);
    try {
      const firestore = getFirestore(app);
      const statsCollection = collection(firestore, "game-log-stats");
      const BATCH_SIZE = 500; // Firestore batch limit
      
      // Find all matching records
      const recordsToDelete = allStats.filter(stat => {
        const matchesEvent = eventsToDelete.some(event => 
          stat.event && stat.event.trim() === event.trim()
        );
        const matchesSeason = stat.season && stat.season === seasonToDelete;
        return matchesEvent && matchesSeason;
      });

      let deletedCount = 0;
      let failedCount = 0;

      // Process in batches
      for (let i = 0; i < recordsToDelete.length; i += BATCH_SIZE) {
        try {
          const batch = writeBatch(firestore);
          const batchRecords = recordsToDelete.slice(i, i + BATCH_SIZE);

          batchRecords.forEach((stat) => {
            const docRef = doc(statsCollection, stat.id);
            batch.delete(docRef);
          });

          await batch.commit();
          deletedCount += batchRecords.length;

          // Show progress for large deletes
          if (recordsToDelete.length > 100) {
            const progress = Math.round((deletedCount / recordsToDelete.length) * 100);
            toast.info(`Deleting... ${deletedCount.toLocaleString()} / ${recordsToDelete.length.toLocaleString()} (${progress}%)`, {
              autoClose: 2000,
            });
          }
        } catch (batchError) {
          // If a batch fails, try to delete individually
          const batchRecords = recordsToDelete.slice(i, i + BATCH_SIZE);
          for (const stat of batchRecords) {
            try {
              const docRef = doc(statsCollection, stat.id);
              await deleteDoc(docRef);
              deletedCount++;
            } catch (rowError) {
              failedCount++;
              console.error(`Failed to delete ${stat.id}:`, rowError);
            }
          }
        }
      }

      // Update local state
      setAllStats(prevStats => 
        prevStats.filter(stat => {
          const matchesEvent = eventsToDelete.some(event => 
            stat.event && stat.event.trim() === event.trim()
          );
          const matchesSeason = stat.season && stat.season === seasonToDelete;
          return !(matchesEvent && matchesSeason);
        })
      );

      setShowMassDeleteModal(false);
      setMassDeleteCount(0);

      if (failedCount === 0) {
        toast.success(`Successfully deleted ${deletedCount.toLocaleString()} record${deletedCount !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`Deleted ${deletedCount.toLocaleString()} record${deletedCount !== 1 ? 's' : ''}, ${failedCount} failed`);
      }

      // Refresh data to ensure consistency
      await fetchAllStats();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error(`Failed to delete records: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageData = getPaginatedData();
      const newSelected = new Set(selectedRows);
      currentPageData.forEach(stat => newSelected.add(stat.id));
      setSelectedRows(newSelected);
    } else {
      const currentPageData = getPaginatedData();
      const newSelected = new Set(selectedRows);
      currentPageData.forEach(stat => newSelected.delete(stat.id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (statId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(statId)) {
      newSelected.delete(statId);
    } else {
      newSelected.add(statId);
    }
    setSelectedRows(newSelected);
  };

  const isAllSelectedOnCurrentPage = () => {
    const currentPageData = getPaginatedData();
    return currentPageData.length > 0 && currentPageData.every(stat => selectedRows.has(stat.id));
  };

  const isSomeSelectedOnCurrentPage = () => {
    const currentPageData = getPaginatedData();
    return currentPageData.some(stat => selectedRows.has(stat.id));
  };

  // Bulk update handler
  const handleBulkUpdate = async () => {
    if (!newTypeValue.trim()) {
      toast.error("Please enter a type value");
      return;
    }

    if (selectedRows.size === 0) {
      toast.error("Please select at least one row to update");
      return;
    }

    setIsUpdating(true);
    try {
      const firestore = getFirestore(app);
      const statsCollection = collection(firestore, "game-log-stats");
      const BATCH_SIZE = 500; // Firestore batch limit
      const selectedIds = Array.from(selectedRows);
      let updatedCount = 0;
      let failedCount = 0;

      // Process in batches
      for (let i = 0; i < selectedIds.length; i += BATCH_SIZE) {
        try {
          const batch = writeBatch(firestore);
          const batchIds = selectedIds.slice(i, i + BATCH_SIZE);

          batchIds.forEach((statId) => {
            const docRef = doc(statsCollection, statId);
            batch.update(docRef, { type: newTypeValue.trim() });
          });

          await batch.commit();
          updatedCount += batchIds.length;

          // Show progress for large updates
          if (selectedIds.length > 100) {
            const progress = Math.round((updatedCount / selectedIds.length) * 100);
            toast.info(`Updating... ${updatedCount} / ${selectedIds.length} (${progress}%)`, {
              autoClose: 2000,
            });
          }
        } catch (batchError) {
          // If a batch fails, try to update individually
          const batchIds = selectedIds.slice(i, i + BATCH_SIZE);
          for (const statId of batchIds) {
            try {
              const docRef = doc(statsCollection, statId);
              await updateDoc(docRef, { type: newTypeValue.trim() });
              updatedCount++;
            } catch (rowError) {
              failedCount++;
              console.error(`Failed to update ${statId}:`, rowError);
            }
          }
        }
      }

      // Update local state
      setAllStats(prevStats => 
        prevStats.map(stat => 
          selectedRows.has(stat.id) ? { ...stat, type: newTypeValue.trim() } : stat
        )
      );

      // Clear selection and close modal
      setSelectedRows(new Set());
      setShowBulkEditModal(false);
      setNewTypeValue("");

      if (failedCount === 0) {
        toast.success(`Successfully updated ${updatedCount} game log${updatedCount !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`Updated ${updatedCount} game log${updatedCount !== 1 ? 's' : ''}, ${failedCount} failed`);
      }

      // Refresh data to ensure consistency
      await fetchAllStats();
    } catch (error) {
      console.error("Error updating game logs:", error);
      toast.error(`Failed to update game logs: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Get paginated data
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredStats.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredStats.length / PAGE_SIZE);

  return (
    <>
      <Header />
      <div className={`${styles.container} ${styles.importPage}`}>
        <Row>
          <Col>
            <h4>Game Log Stats View</h4>
            
            {/* Stats Count and Filters */}
            <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
              <div>
                <p style={{ margin: 0, fontSize: "16px" }}>
                  <strong>Total Stats:</strong> {allStats.length.toLocaleString()}
                  {hasActiveFilters && (
                    <span style={{ marginLeft: "15px", color: "#007bff" }}>
                      <strong>Filtered:</strong> {filteredStats.length.toLocaleString()}
                    </span>
                  )}
                  {filteredStats.length > PAGE_SIZE && (
                    <span style={{ marginLeft: "15px", color: "#666" }}>
                      | <strong>Showing:</strong> {((currentPage - 1) * PAGE_SIZE + 1).toLocaleString()} - {Math.min(currentPage * PAGE_SIZE, filteredStats.length).toLocaleString()} (Page {currentPage} of {totalPages})
                    </span>
                  )}
                </p>
              </div>
              
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <Form.Group style={{ margin: 0 }}>
                  <Form.Label style={{ marginRight: "5px", fontSize: "14px" }}>Date:</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    style={{ width: "150px" }}
                  />
                </Form.Group>
                
                <Form.Group style={{ margin: 0 }}>
                  <Form.Label style={{ marginRight: "5px", fontSize: "14px" }}>Event:</Form.Label>
                  <Form.Control
                    as="select"
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    style={{ width: "200px" }}
                  >
                    <option value="">All Events</option>
                    {uniqueEvents.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                
                <Form.Group style={{ margin: 0 }}>
                  <Form.Label style={{ marginRight: "5px", fontSize: "14px" }}>Type:</Form.Label>
                  <Form.Control
                    as="select"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    style={{ width: "200px" }}
                  >
                    <option value="">All Types</option>
                    {uniqueTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                
                <Form.Group style={{ margin: 0 }}>
                  <Form.Label style={{ marginRight: "5px", fontSize: "14px" }}>Season:</Form.Label>
                  <Form.Control
                    as="select"
                    value={seasonFilter}
                    onChange={(e) => setSeasonFilter(e.target.value)}
                    style={{ width: "150px" }}
                  >
                    <option value="">All Seasons</option>
                    {uniqueSeasons.map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                
                {hasActiveFilters && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {/* Stats Table */}
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>Loading stats...</p>
              </div>
            ) : filteredStats.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>No stats found{hasActiveFilters ? " matching your filters" : ""}.</p>
              </div>
            ) : (
              <>
                {/* Bulk Edit Controls */}
                {selectedRows.size > 0 && (
                  <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#e7f3ff", borderRadius: "5px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                    <div>
                      <strong>{selectedRows.size}</strong> row{selectedRows.size !== 1 ? 's' : ''} selected
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowBulkEditModal(true)}
                      >
                        Bulk Edit Type
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setSelectedRows(new Set())}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}

                {filteredStats.length > PAGE_SIZE && (
                  <div style={{ marginBottom: "15px", display: "flex", gap: "10px", alignItems: "center" }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </Button>
                  </div>
                )}

                <div style={{ maxHeight: "600px", overflowY: "auto", overflowX: "auto", marginBottom: "20px" }}>
                  <Table striped bordered hover responsive className={styles.statsTable} style={{ width: "auto", tableLayout: "auto" }}>
                    <thead className={styles.tableHeader} style={{ position: "sticky", top: 0, backgroundColor: "#000", zIndex: 10 }}>
                      <tr>
                        <th style={{ width: "50px", textAlign: "center" }}>
                          <Form.Check
                            type="checkbox"
                            checked={isAllSelectedOnCurrentPage()}
                            ref={(input) => {
                              if (input) input.indeterminate = isSomeSelectedOnCurrentPage() && !isAllSelectedOnCurrentPage();
                            }}
                            onChange={handleSelectAll}
                          />
                        </th>
                        {tableColumns.map(col => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedData().map((stat) => (
                        <tr 
                          key={stat.id} 
                          className={styles.tableRow}
                          style={{ backgroundColor: selectedRows.has(stat.id) ? "#e7f3ff" : undefined }}
                        >
                          <td style={{ textAlign: "center" }}>
                            <Form.Check
                              type="checkbox"
                              checked={selectedRows.has(stat.id)}
                              onChange={() => handleSelectRow(stat.id)}
                            />
                          </td>
                          {tableColumns.map(col => (
                            <td key={col} className={styles.tableCell}>
                              {stat[col] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div style={{ marginTop: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Button
                variant="primary"
                onClick={fetchAllStats}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Refresh Data"}
              </Button>
              
              <Button
                variant="danger"
                onClick={handleMassDeleteConfirm}
                disabled={isLoading || isDeleting}
              >
                Mass Delete: 3SSB Events (2025)
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Bulk Edit Modal */}
      <Modal show={showBulkEditModal} onHide={() => !isUpdating && setShowBulkEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Edit Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are about to update the <strong>type</strong> field for <strong>{selectedRows.size}</strong> selected game log{selectedRows.size !== 1 ? 's' : ''}.</p>
          
          <Form.Group style={{ marginTop: "20px" }}>
            <Form.Label><strong>New Type Value:</strong></Form.Label>
            <Form.Control
              type="text"
              value={newTypeValue}
              onChange={(e) => setNewTypeValue(e.target.value)}
              placeholder="e.g., Camp"
              disabled={isUpdating}
            />
            <Form.Text className="text-muted">
              Common types: Camp, Game, Practice, etc.
            </Form.Text>
          </Form.Group>

          {newTypeValue.trim() && (
            <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#fff3cd", borderRadius: "5px" }}>
              <strong>Preview:</strong> All selected rows will have their type changed to "<strong>{newTypeValue.trim()}</strong>"
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowBulkEditModal(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkUpdate}
            disabled={isUpdating || !newTypeValue.trim()}
          >
            {isUpdating ? "Updating..." : `Update ${selectedRows.size} Row${selectedRows.size !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mass Delete Confirmation Modal */}
      <Modal show={showMassDeleteModal} onHide={() => !isDeleting && setShowMassDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Mass Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "18px", marginBottom: "15px" }}>
              <strong>⚠️ Warning: This action cannot be undone!</strong>
            </p>
            <p style={{ marginBottom: "10px" }}>
              You are about to delete <strong style={{ color: "#dc3545", fontSize: "20px" }}>{massDeleteCount.toLocaleString()}</strong> record{massDeleteCount !== 1 ? 's' : ''} matching:
            </p>
            <div style={{ marginLeft: "20px", marginBottom: "15px" }}>
              <p style={{ marginBottom: "5px" }}><strong>Events:</strong></p>
              <ul style={{ marginBottom: "10px" }}>
                <li>3SSB 15U</li>
                <li>3SSB 16U</li>
                <li>3SSB 17U</li>
              </ul>
              <p style={{ marginBottom: "5px" }}><strong>Season:</strong> 2025</p>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#f8d7da", borderRadius: "5px", border: "1px solid #f5c6cb" }}>
              <p style={{ margin: 0, color: "#721c24" }}>
                <strong>This will permanently delete all matching records from the database.</strong>
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowMassDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleMassDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : `Yes, Delete ${massDeleteCount.toLocaleString()} Record${massDeleteCount !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

